import { History, Pencil, Plus, Trash2, Zap } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { describeAutomationRule } from '@/lib/automations';
import type { AutomationRule } from '@/api/generated/client';

interface AutomationsContentProps {
  rules: AutomationRule[];
  isLoading: boolean;
  error: unknown;
  templateNameById: Map<number, string>;
  onCreate: () => void;
  onEdit: (rule: AutomationRule) => void;
  onShowRuns: (rule: AutomationRule) => void;
  onRequestDelete: (rule: AutomationRule) => void;
  onToggleActive: (rule: AutomationRule, isActive: boolean) => void;
}

export function AutomationsContent({
  rules,
  isLoading,
  error,
  templateNameById,
  onCreate,
  onEdit,
  onShowRuns,
  onRequestDelete,
  onToggleActive,
}: AutomationsContentProps) {
  return (
    <div className="container mx-auto px-4 py-8 space-y-6">
      <div className="flex items-center justify-between max-md:flex-col max-md:items-stretch max-md:gap-3">
        <PageHeader
          title="Automations"
          description="Set up if-then rules that run automatically when things happen in TurboClaim"
          className="max-md:mb-0"
        />
        <Button onClick={onCreate} className="max-md:w-full">
          <Plus className="mr-2 h-4 w-4" />
          New Automation
        </Button>
      </div>

      {isLoading && <p className="text-sm text-muted-foreground">Loading automations…</p>}

      {!isLoading && Boolean(error) && (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
            <Zap className="h-8 w-8 text-destructive" />
            <p className="font-medium">Couldn&apos;t load automations</p>
            <p className="max-w-md text-sm text-muted-foreground">
              Something went wrong while loading your automations. Refresh the page to try again.
            </p>
          </CardContent>
        </Card>
      )}

      {!isLoading && !error && rules.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
            <Zap className="h-8 w-8 text-muted-foreground" />
            <p className="font-medium">No automations yet</p>
            <p className="max-w-md text-sm text-muted-foreground">
              Create your first automation to automatically send a form to the homeowner whenever a
              claim is created - for example, from ClaimPredict.
            </p>
            <Button onClick={onCreate} className="max-md:w-full">
              <Plus className="mr-2 h-4 w-4" />
              New Automation
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="space-y-3">
        {rules.map((rule) => (
          <Card key={rule.id}>
            <CardContent className="flex items-center justify-between gap-4 py-4 max-md:flex-col max-md:items-start max-md:gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="truncate font-medium">{rule.name}</p>
                  {!rule.isActive && <Badge variant="secondary">Paused</Badge>}
                </div>
                <p className="mt-1 text-sm text-muted-foreground">
                  {describeAutomationRule(rule, templateNameById.get(rule.actionConfig.formTemplateId))}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2 max-md:w-full max-md:justify-end">
                <Switch
                  checked={rule.isActive}
                  onCheckedChange={(checked) => onToggleActive(rule, checked)}
                  aria-label={`Toggle ${rule.name}`}
                />
                <Button variant="ghost" size="icon" onClick={() => onShowRuns(rule)} title="Run history">
                  <History className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => onEdit(rule)} title="Edit">
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => onRequestDelete(rule)} title="Delete">
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
